<template>
    <slot />
</template>

<script setup>
    import { Color, DoubleSide, Mesh, RepeatWrapping } from 'three';
    import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue';

    import { simplePlaneGeometry } from '@resn/gozer-three';
    import { CustomMaterial } from '@resn/gozer-three/materials';
    import { usePane, useRafBool, useThreeObject } from '@resn/gozer-vue';
    import gsap from '@resn/gsap';

    import glslFill from '@/libs/lygia/draw/fill.glsl';
    import glslConst from '@/libs/lygia/math/const.glsl';
    import glslCircleSdf from '@/libs/lygia/sdf/circleSDF.glsl';
    import glslRotate from '@/libs/lygia/space/rotate.glsl';
    import {
        uniforms as matcapUniforms,
        shaderChunks as ShaderChunksMatcap,
    } from '~/components/gl/shaders/ShaderChunks/matcap';
    import { glslSpeedLines } from '~/components/gl/shaders/ShaderChunks/speed-lines';
    import { useModelExporter } from '~/composables/useModelExporter';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { useMatcap } from '~/providers/MatcapProvider';

    const props = defineProps({
        layer: {
            type: Number,
            default: 0,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    });

    const { rotation } = useMatcap();

    const v = computed(() => props.visible);

    const active = ref(true);
    const progress = ref(1);
    const uSwirlProgress = ref(0);
    const uSpinSpeed = ref(0.2);
    const uToonThreshold = ref(0.25);
    const uRadiusScale = ref(0.05);
    const uAngleScale = ref(12.83);

    const color = ref('#84eaff');

    const material = new CustomMaterial({
        uniforms: {
            uTime: 0,
            uProgress: progress.value,
            uSwirlProgress: 0,
            uSpinSpeed: 0,
            uNoiseMap: null,
            uToonThreshold: 0,
            uRadiusScale: 0,
            uAngleScale: 0,
            uColor: new Color(),

            ...matcapUniforms(),
        },
        vs: /* glsl */ `
            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vMvPosition;

            void main() {
                vUv = uv;

                vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                gl_Position = projectionMatrix * mvPosition;

                vNormal = normalize(normalMatrix * normal); // view-space normal
                vMvPosition = -mvPosition.xyz;
            }
        `,
        fs: /* glsl */ `
            uniform float uTime;
            uniform float uProgress;
            uniform float uSwirlProgress;
            uniform float uSpinSpeed;
            uniform sampler2D uNoiseMap;
            uniform float uToonThreshold;
            uniform float uRadiusScale;
            uniform float uAngleScale;
            uniform vec3 uColor;
            // uniform sampler2D tMatcap;

            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vMvPosition;

            ${glslConst}
            ${glslCircleSdf}
            ${glslRotate}
            ${glslFill}
            ${glslSpeedLines}
            ${ShaderChunksMatcap.uniforms}
            ${ShaderChunksMatcap.funcs}

            void main() {
                vec3 normal = normalize(vNormal);

                float radius = length(vUv - 0.5);
        
                float spinSpeed = uTime * uSpinSpeed;
                vec2 noiseUv = vUv + vec2(0.5, 0.0);
                float speedLinesNoise1 = speedLines(
                    uNoiseMap,
                    noiseUv,
                    spinSpeed,  
                    uRadiusScale,
                    uAngleScale
                );
                float speedLinesNoise2 = speedLines(
                    uNoiseMap,
                    noiseUv,
                    spinSpeed * 0.25,  
                    uRadiusScale,
                    uAngleScale * 0.25
                );
                
                float sdf = mix(speedLinesNoise1, speedLinesNoise2, 0.5);
                
                float mask = 1.0 - fill(sdf, mix(0.5, uToonThreshold, uProgress));

                // vec3 cMatcap = texture2D(tMatcap, vUv).rgb;
                vec3 cMatcap = matcap(tMatcap, normal, vMvPosition, false, mask, 1.0, 1.0, u_matcapRotation);
                
                float alpha = mask;

                if (alpha == 0.0) discard;

                gl_FragColor = vec4(cMatcap, alpha);
                // gl_FragColor = vec4(vec3(mask), 1.0);
                
                #include <tonemapping_fragment>
                #include <colorspace_fragment>
            }
        `,
        options: {
            name: 'Ultra Bg',
            // side: DoubleSide,
            depthWrite: false,
        },
    });

    const mesh = new Mesh(simplePlaneGeometry, material);

    useThreeObject(mesh, { name: 'Ultra Bg', props: { v } });

    mesh.position.z = -0.15;
    mesh.scale.set(1, 2, 1);
    mesh.layers.set(props.layer);
    mesh.renderOrder = -1;

    useGlobalAssets(({ data }) => {
        const { noise1, matcapIrridescent3 } = data;
        noise1.wrapS = noise1.wrapT = RepeatWrapping;
        noise1.needsUpdate = true;

        material.uniforms.uNoiseMap.value = noise1;
        material.uniforms.tMatcap.value = matcapIrridescent3;
    });

    let tl = null;
    const show = ({ delay = 0 } = {}) => {
        tl?.kill();

        const duration = 0.5;

        tl = gsap.timeline({
            delay,
            onStart: () => (active.value = true),
        });
        tl.fromTo(progress, { value: 0 }, { value: 1, duration });
    };

    const hide = ({ delay = 0, swirl = true } = {}) => {
        tl?.kill();

        const duration = 0.5;

        tl = gsap.timeline({
            delay,
            onStart: () => (active.value = true),
            onComplete: () => (active.value = false),
        });
        tl.to(progress, { value: 0, duration });
    };

    watch(progress, (val) => {
        material.uniforms.uProgress.value = val;
    });

    watchEffect(() => {
        material.uniforms.uSpinSpeed.value = uSpinSpeed.value;
        material.uniforms.uToonThreshold.value = uToonThreshold.value;
        material.uniforms.uRadiusScale.value = uRadiusScale.value;
        material.uniforms.uAngleScale.value = uAngleScale.value;
        material.uniforms.uColor.value.set(color.value);
    });

    useRafBool(active, ({ timestamp }) => {
        material.uTime = timestamp * 0.001;
        material.u_matcapRotation = rotation.value;
    });

    const destroy = () => {
        material.dispose();
    };

    onBeforeUnmount(destroy);

    defineExpose({ show, hide });

    const paneOptions = {
        min: 0,
        max: 1,
        step: 0.01,
    };

    const modelExport = useModelExporter();

    const showPane = false;
    if (!modelExport && showPane) {
        usePane(
            [
                { name: 'progress', value: progress, options: paneOptions },
                { name: 'uSwirlProgress', value: uSwirlProgress, options: paneOptions },
                { name: 'uSpinSpeed', value: uSpinSpeed, options: { min: 0, max: 3, step: 0.01 } },
                { name: 'uToonThreshold', value: uToonThreshold, options: paneOptions },
                {
                    name: 'uRadiusScale',
                    value: uRadiusScale,
                    options: { min: 0, max: 0.2, step: 0.001 },
                },
                {
                    name: 'uAngleScale',
                    value: uAngleScale,
                    options: { min: 0, max: 20, step: 0.01 },
                },
                { name: 'active', value: active },
                { name: 'color', value: color },
            ],
            { title: 'Ultra Bg', expanded: false }
        );
    }
</script>
